import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, FileText, Calendar, MapPin, DollarSign, Users, Check, X, Clock } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import { apiService } from '../../services/api'
import { toast } from 'react-toastify'

interface RfqResponse {
  id: string
  responseText: string
  proposedPrice?: number
  deliveryDays?: number
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED'
  createdAt: string
  vendor: {
    id: string
    firstName: string
    lastName: string
    companyName?: string
    email: string
  }
}

interface RfqDetail {
  id: string
  title: string
  description: string
  deadline: string
  budgetRange?: string
  location?: string
  isOpen: boolean
  createdAt: string
  buyer: {
    id: string
    companyName?: string
    firstName: string
    lastName: string
    email: string
  }
  responses: RfqResponse[]
  _count: {
    responses: number
  }
}

export const RfqDetailPage: React.FC = () => {
  const { rfqId } = useParams<{ rfqId: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [rfqDetail, setRfqDetail] = useState<RfqDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showRejectionModal, setShowRejectionModal] = useState(false)
  const [selectedResponseId, setSelectedResponseId] = useState<string | null>(null)
  const [rejectionReason, setRejectionReason] = useState('')
  const [rejectionComment, setRejectionComment] = useState('')
  const [processingAction, setProcessingAction] = useState(false)

  useEffect(() => {
    if (!user) {
      navigate('/auth/login')
      return
    }

    // Check if user has access to RFQ details
    const isEnterpriseUser = user.role === 'ENTERPRISE_BUYER' || 
                           user.role === 'ENTERPRISE_VENDOR' || 
                           (user.role === 'BUYER' && user.isEnterprise) ||
                           (user.role === 'VENDOR' && user.isEnterprise)

    if (!isEnterpriseUser) {
      navigate('/', { replace: true })
      return
    }

    if (rfqId) {
      fetchRfqDetail()
    }
  }, [user, navigate, rfqId])

  const fetchRfqDetail = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiService.get<any>(`/api/enterprise/rfqs/${rfqId}`)
      if (response.success) {
        setRfqDetail(response.data)
      } else {
        setError('Failed to fetch RFQ details')
      }
    } catch (err) {
      console.error('Error fetching RFQ details:', err)
      setError('Failed to fetch RFQ details')
    } finally {
      setLoading(false)
    }
  }

  const handleAccept = async (responseId: string) => {
    setProcessingAction(true)
    try {
      const response = await apiService.patch<any>(`/api/enterprise/rfqs/responses/${responseId}/status`, {
        status: 'ACCEPTED'
      })
      
      if (response.success) {
        toast.success('Response accepted successfully!')
        fetchRfqDetail()
      } else {
        throw new Error(response.message || 'Failed to accept response')
      }
    } catch (error: any) {
      console.error('Error accepting response:', error)
      toast.error(error.response?.data?.message || 'Failed to accept response')
    } finally {
      setProcessingAction(false)
    }
  }

  const handleReject = (responseId: string) => {
    setSelectedResponseId(responseId)
    setRejectionReason('')
    setRejectionComment('')
    setShowRejectionModal(true)
  }

  const handleRejectConfirm = async () => {
    if (!selectedResponseId || !rejectionReason) {
      toast.error('Please provide a rejection reason')
      return
    }

    setProcessingAction(true)
    try {
      const response = await apiService.patch<any>(`/api/enterprise/rfqs/responses/${selectedResponseId}/status`, {
        status: 'REJECTED',
        rejectionReason,
        rejectionComment
      })
      
      if (response.success) {
        toast.success('Response rejected successfully!')
        setShowRejectionModal(false)
        fetchRfqDetail()
      } else {
        throw new Error(response.message || 'Failed to reject response')
      }
    } catch (error: any) {
      console.error('Error rejecting response:', error)
      toast.error(error.response?.data?.message || 'Failed to reject response')
    } finally {
      setProcessingAction(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACCEPTED':
        return 'bg-green-100 text-green-800'
      case 'REJECTED':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-yellow-100 text-yellow-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ACCEPTED':
        return <Check className="h-4 w-4" />
      case 'REJECTED':
        return <X className="h-4 w-4" />
      default:
        return <Clock className="h-4 w-4" />
    }
  }

  const isBuyer = user?.role === 'ENTERPRISE_BUYER' || (user?.role === 'BUYER' && user?.isEnterprise)

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading RFQ details...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">{error}</div>
          <button
            onClick={fetchRfqDetail}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!rfqDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">RFQ Not Found</h3>
          <p className="text-gray-600 mb-6">The RFQ you're looking for doesn't exist or you don't have access to it.</p>
          <button
            onClick={() => navigate('/enterprise/rfqs')}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg"
          >
            Back to RFQs
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/enterprise/rfqs')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <ArrowLeft className="h-6 w-6" />
                </button>
                <h1 className="text-xl font-semibold text-gray-900">
                  RFQ Details
                </h1>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* RFQ Information */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      {rfqDetail.title}
                    </h2>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        rfqDetail.isOpen ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {rfqDetail.isOpen ? 'Open' : 'Closed'}
                      </span>
                      <span>Created {formatDate(rfqDetail.createdAt)}</span>
                    </div>
                  </div>
                </div>

                <div className="prose max-w-none mb-6">
                  <p className="text-gray-700 whitespace-pre-wrap">{rfqDetail.description}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {rfqDetail.budgetRange && (
                    <div className="flex items-center text-sm text-gray-600">
                      <DollarSign className="h-4 w-4 mr-2" />
                      <span>Budget: {rfqDetail.budgetRange}</span>
                    </div>
                  )}
                  {rfqDetail.location && (
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="h-4 w-4 mr-2" />
                      <span>Location: {rfqDetail.location}</span>
                    </div>
                  )}
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span>Deadline: {formatDate(rfqDetail.deadline)}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Users className="h-4 w-4 mr-2" />
                    <span>{rfqDetail._count.responses} responses</span>
                  </div>
                </div>

                {/* Buyer Information */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Buyer Information</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="font-medium">
                      {rfqDetail.buyer.companyName || `${rfqDetail.buyer.firstName} ${rfqDetail.buyer.lastName}`}
                    </p>
                    <p className="text-sm text-gray-600">{rfqDetail.buyer.email}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Responses */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Vendor Responses ({rfqDetail.responses.length})
                </h3>
                
                {rfqDetail.responses.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No responses received yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {rfqDetail.responses.map((response) => (
                      <div key={response.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <p className="font-medium">
                              {response.vendor.companyName || `${response.vendor.firstName} ${response.vendor.lastName}`}
                            </p>
                            <p className="text-sm text-gray-500">{response.vendor.email}</p>
                          </div>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(response.status)}`}>
                            {getStatusIcon(response.status)}
                            <span className="ml-1">{response.status}</span>
                          </span>
                        </div>
                        
                        <div className="mb-3">
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{response.responseText}</p>
                        </div>
                        
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                          {response.proposedPrice && (
                            <div className="flex items-center">
                              <DollarSign className="h-4 w-4 mr-1" />
                              <span>${response.proposedPrice.toLocaleString()}</span>
                            </div>
                          )}
                          {response.deliveryDays && (
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1" />
                              <span>{response.deliveryDays} days</span>
                            </div>
                          )}
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            <span>{formatDate(response.createdAt)}</span>
                          </div>
                        </div>
                        
                        {/* Action Buttons for Buyers */}
                        {isBuyer && response.status === 'PENDING' && (
                          <div className="flex space-x-3 pt-3 border-t border-gray-100">
                            <button
                              onClick={() => handleAccept(response.id)}
                              disabled={processingAction}
                              className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Accept
                            </button>
                            <button
                              onClick={() => handleReject(response.id)}
                              disabled={processingAction}
                              className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50"
                            >
                              <X className="h-4 w-4 mr-1" />
                              Reject
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Response</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Reason *
              </label>
              <select
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select a reason</option>
                <option value="Price too high">Price too high</option>
                <option value="Delivery time too long">Delivery time too long</option>
                <option value="Does not meet specifications">Does not meet specifications</option>
                <option value="Found better alternative">Found better alternative</option>
                <option value="Project cancelled">Project cancelled</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Comments (Optional)
              </label>
              <textarea
                value={rejectionComment}
                onChange={(e) => setRejectionComment(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Provide additional details..."
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowRejectionModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={processingAction}
              >
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={processingAction || !rejectionReason}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {processingAction ? 'Processing...' : 'Reject Response'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
} 